import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Checkbox,
  Spin,
  Select,
  Tag,
  Tree,
  Empty,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronRight, Search, Check, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  multichoixService,
  multichoixKeys,
} from '../../../services/multichoixService';
import TreeSearchInput from '@client/components/ui/TreeSearchInput';
import axiosInstance from '@client/../services/api';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';

// Special value for weeds without a group
const WEED_NO_GROUP_VALUE = '__no_group__';

// ============================================================================
// LANGUAGE SELECTOR COMPONENT
// ============================================================================
const LanguageSelector = React.memo(({ value, options, onChange }) => (
  <Select
    size="small"
    value={value}
    onChange={onChange}
    options={options}
    className="language-selector"
    suffixIcon={<GlobalOutlined />}
    optionLabelProp="label"
    style={{ minWidth: 110 }}
  />
));
LanguageSelector.displayName = 'LanguageSelector';

const EnemiesStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  allLabels,
  onChange,
  onForceRevalidation,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  const [language, setLanguage] = useState('fr');
  const [expandedKeys, setExpandedKeys] = useState({});
  const [searchValues, setSearchValues] = useState({});
  // State for disease list mode: 'organism' (by hierarchy) or 'grouping' (by regroupement)
  const [diseaseListMode, setDiseaseListMode] = useState('organism');
  // State for weed category filters (multiple selection via checkboxes)
  const [weedCategoryFilters, setWeedCategoryFilters] = useState([]);
  // Mobile sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(null); // null or enemy type string
  const [mobileExpandedNodes, setMobileExpandedNodes] = useState({});

  // Use props directly as source of truth - no local state duplication needed
  // This ensures selections persist when the component unmounts/remounts
  const checkedKeys = value || [];
  const checkedLabels = selectionLabels || [];

  // Transform selections for the API, excluding current step's own selections (enemies)
  // to prevent refetching when the user checks/unchecks an enemy.
  // Step 4's available options only depend on steps 1-3.
  const apiSelections = useMemo(() => {
    const transformed = multichoixService.transformSelectionsForApi(allSelections || {});
    const { enemies, ...previousStepSelections } = transformed;
    return previousStepSelections;
  }, [allSelections]);

  // Fetch enemies from multichoix API (step 4)
  const { data, isLoading, error } = useQuery({
    queryKey: multichoixKeys.stepOptions(4, apiSelections),
    queryFn: () => multichoixService.getStepOptions(4, apiSelections),
    enabled: !!apiSelections.cultures?.length,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch weed groups from API for dynamic checkbox filtering
  const { data: weedGroupsData } = useQuery({
    queryKey: ['weedGroups'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/enemies/weeds-groups/', {
        params: { limit: 100 }, // Fetch all groups
      });
      return response.data.results || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - groups don't change often
  });

  // Memoize weed groups list - filter out "Autres" since we have a dedicated "no group" checkbox
  const weedGroups = useMemo(() => {
    const groups = weedGroupsData || [];
    // Filter out groups named "Autres" (case-insensitive) to avoid duplicate with our "no group" option
    return groups.filter((group) => group.label?.toLowerCase() !== 'autres');
  }, [weedGroupsData]);

  // Get display name based on language
  const getDisplayName = useCallback(
    (item) => {
      if (!item) return '';
      switch (language) {
        case 'en':
          return item.english_name || item.french_name || item.label || '';
        case 'latin':
          return item.latin_name || item.french_name || item.label || '';
        default:
          return item.client_display_name || item.french_name || item.label || '';
      }
    },
    [language]
  );

  // Convert API data to Ant Design Tree format
  const convertToTreeData = useCallback(
    (children, enemyType) => {
      if (!children) return [];

      return children.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const node = {
          key: item.id,
          title: getDisplayName(item),
          isLeaf: !hasChildren,
          selectable: true,
          checkable: true,
          enemyData: item,
          enemyType,
        };

        if (hasChildren) {
          node.children = convertToTreeData(item.children, enemyType);
        }

        return node;
      });
    },
    [getDisplayName]
  );

  // Helper to get all descendant keys from a node
  const getAllDescendantKeys = useCallback((node) => {
    const keys = [];
    if (node.children) {
      node.children.forEach((child) => {
        keys.push(child.key);
        keys.push(...getAllDescendantKeys(child));
      });
    }
    return keys;
  }, []);

  // Helper to find a node by key in tree data
  const findNodeByKey = useCallback((nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Get all keys from tree data
  const getAllKeys = useCallback((nodes) => {
    let keys = [];
    for (const node of nodes) {
      keys.push(node.key);
      if (node.children) {
        keys = keys.concat(getAllKeys(node.children));
      }
    }
    return keys;
  }, []);


  // Filter tree data based on search value
  const filterTreeData = useCallback((nodes, searchValue) => {
    if (!searchValue) return nodes;

    const lowerSearch = searchValue.toLowerCase();

    const filterNodes = (nodeList) => {
      return nodeList
        .map((node) => {
          const titleMatch = node.title.toLowerCase().includes(lowerSearch);
          const filteredChildren = node.children
            ? filterNodes(node.children)
            : [];

          if (titleMatch || filteredChildren.length > 0) {
            return {
              ...node,
              children:
                filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterNodes(nodes);
  }, []);



  // Filter enemies by weed group (using weed_group.id)
  // Works recursively on the tree structure
  // Both parent and child nodes can have weed_group assigned
  const filterByWeedCategory = useCallback((enemies, selectedGroupIds) => {
    if (!selectedGroupIds || selectedGroupIds.length === 0) {
      return enemies; // No filter applied, return all
    }

    // Helper to check if an enemy matches the filter (parent or leaf)
    const matchesFilter = (enemy) => {
      const weedGroup = enemy.weed_group;
      if (!weedGroup || !weedGroup.id) {
        // Include enemies without weed_group only if 'no group' option is selected
        return selectedGroupIds.includes(WEED_NO_GROUP_VALUE);
      }
      return selectedGroupIds.includes(weedGroup.id);
    };

    // Recursively filter the tree
    const filterTree = (nodes) => {
      return nodes
        .map((node) => {
          // Check if this node itself matches the filter
          const nodeMatches = matchesFilter(node);

          // If node has children, filter them recursively
          if (node.children && node.children.length > 0) {
            const filteredChildren = filterTree(node.children);

            // Keep parent if:
            // 1. The parent node itself matches the filter, OR
            // 2. It has children that match the filter
            if (nodeMatches || filteredChildren.length > 0) {
              return {
                ...node,
                // If parent matches, keep all children; otherwise only matching children
                children: nodeMatches ? node.children : filteredChildren,
              };
            }
            // Neither parent nor children match - remove
            return null;
          }

          // Leaf node - check if it matches the filter
          return nodeMatches ? node : null;
        })
        .filter(Boolean); // Remove null entries
    };

    return filterTree(enemies);
  }, []);

  // Handle tree check with custom behavior
  const handleCheck = useCallback(
    (_checkedKeysValue, info, enemyType, treeData) => {
      const { node, checked } = info;

      // Get all keys in current tree to determine which IDs belong to this enemy type
      const allKeysInCurrentTree = getAllKeys(treeData);

      // Get current checked keys for this enemy type
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const currentTypeKeys = checkedKeys.filter((id) => {
        const label = checkedLabels.find((l) => l.value === id);
        if (label && label.enemyType) {
          return label.enemyType === enemyType;
        }
        // Cold start fallback: check if ID exists in this tree
        return allKeysInCurrentTree.includes(id);
      });

      let newTypeKeys = [...currentTypeKeys];

      if (checked) {
        // When checking a node, add it and all its descendants
        newTypeKeys.push(node.key);
        const descendantKeys = getAllDescendantKeys(node);
        descendantKeys.forEach((key) => {
          if (!newTypeKeys.includes(key)) {
            newTypeKeys.push(key);
          }
        });
      } else {
        // When unchecking a node, only remove that specific node (not the parent)
        newTypeKeys = newTypeKeys.filter((key) => key !== node.key);
        // Also uncheck all descendants if unchecking a parent
        const descendantKeys = getAllDescendantKeys(node);
        newTypeKeys = newTypeKeys.filter(
          (key) => !descendantKeys.includes(key)
        );
      }

      // Remove duplicates
      newTypeKeys = [...new Set(newTypeKeys)];

      // Get all currently checked items across other enemy types
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const otherTypeItems = checkedLabels.filter((item) => {
        if (item.enemyType) {
          return item.enemyType !== enemyType;
        }
        // Cold start fallback: exclude items that exist in current tree
        return !allKeysInCurrentTree.includes(item.value);
      });
      const otherTypeIds = checkedKeys.filter((id) => {
        const existingLabel = checkedLabels.find((l) => l.value === id);
        if (existingLabel && existingLabel.enemyType) {
          return existingLabel.enemyType !== enemyType;
        }
        // Cold start fallback: exclude IDs that exist in current tree
        return !allKeysInCurrentTree.includes(id);
      });

      // Build labels for the new type keys
      const newTypeItems = newTypeKeys.map((key) => {
        const foundNode = findNodeByKey(treeData, key);
        return {
          value: key,
          label: foundNode ? getDisplayName(foundNode.enemyData) : key,
          enemyType,
        };
      });

      // Combine with other types
      const allIds = [...otherTypeIds, ...newTypeKeys];
      const allLabelsArr = [...otherTypeItems, ...newTypeItems];

      // Only call onChange - parent state is source of truth
      onChange(allIds, allLabelsArr);
    },
    [
      checkedKeys,
      checkedLabels,
      getDisplayName,
      onChange,
      getAllDescendantKeys,
      findNodeByKey,
      getAllKeys,
    ]
  );

  // Handle select all for a specific enemy type
  const handleSelectAll = useCallback(
    (enemyType, treeData, checked) => {
      const allTypeKeys = getAllKeys(treeData);

      // Get current checked items for other types
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const otherTypeItems = checkedLabels.filter((item) => {
        if (item.enemyType) {
          return item.enemyType !== enemyType;
        }
        // Cold start fallback: exclude items that exist in current tree
        return !allTypeKeys.includes(item.value);
      });
      const otherTypeIds = checkedKeys.filter((id) => {
        const existingLabel = checkedLabels.find((l) => l.value === id);
        if (existingLabel && existingLabel.enemyType) {
          return existingLabel.enemyType !== enemyType;
        }
        // Cold start fallback: exclude IDs that exist in current tree
        return !allTypeKeys.includes(id);
      });

      if (checked) {
        // Select all items in this type
        const newTypeItems = allTypeKeys.map((key) => {
          const foundNode = findNodeByKey(treeData, key);
          return {
            value: key,
            label: foundNode ? getDisplayName(foundNode.enemyData) : key,
            enemyType,
          };
        });

        const allIds = [...otherTypeIds, ...allTypeKeys];
        const allLabelsArr = [...otherTypeItems, ...newTypeItems];

        onChange(allIds, allLabelsArr);
      } else {
        // Deselect all items in this type
        onChange(otherTypeIds, otherTypeItems);
      }
    },
    [
      checkedKeys,
      checkedLabels,
      getAllKeys,
      findNodeByKey,
      getDisplayName,
      onChange,
    ]
  );

  // Handle expand
  const handleExpand = useCallback((keys, enemyType) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [enemyType]: keys,
    }));
  }, []);

  // Handle remove tag
  const handleRemoveTag = useCallback(
    (itemToRemove) => {
      const newKeys = checkedKeys.filter((key) => key !== itemToRemove.value);
      const newLabels = checkedLabels.filter(
        (item) => item.value !== itemToRemove.value
      );

      onChange(newKeys, newLabels);
    },
    [checkedKeys, checkedLabels, onChange]
  );

  // Handle weed category checkbox change
  const handleWeedCategoryChange = useCallback((category, checked) => {
    setWeedCategoryFilters((prev) => {
      if (checked) {
        return [...prev, category];
      }
      return prev.filter((c) => c !== category);
    });
  }, []);

  // Get checked keys for a specific enemy type
  // Handles both interactive selection (label has enemyType) and cold start restoration (no enemyType)
  const getCheckedKeysForType = useCallback(
    (enemyType, treeData) => {
      const allKeysInTree = getAllKeys(treeData);
      return checkedKeys.filter((id) => {
        const label = checkedLabels.find((l) => l.value === id);
        // If label has enemyType, use it for filtering
        if (label && label.enemyType) {
          return label.enemyType === enemyType;
        }
        // Cold start fallback: check if ID exists in this enemy type's tree data
        return allKeysInTree.includes(id);
      });
    },
    [checkedKeys, checkedLabels, getAllKeys]
  );

  // Check if all items are selected for a type
  const isAllSelectedForType = useCallback(
    (enemyType, treeData) => {
      const allKeys = getAllKeys(treeData);
      const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
      return (
        allKeys.length > 0 &&
        allKeys.every((key) => typeCheckedKeys.includes(key))
      );
    },
    [getAllKeys, getCheckedKeysForType]
  );

  // Enemy type labels
  const enemyTypeLabels = {
    pest: t('multichoix.steps.enemies.pests', 'RAVAGEURS'),
    disease: t('multichoix.steps.enemies.diseases', 'MALADIES'),
    weeds: t('multichoix.steps.enemies.weeds', 'MAUVAISES HERBES'),
  };

  // Language options for the selector
  const languageOptions = useMemo(
    () => [
      { value: 'fr', label: 'Français' },
      { value: 'en', label: 'English' },
      { value: 'latin', label: 'Latin' },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" />
        <span className="ml-3">
          {t('multichoix.common.loading', 'Chargement...')}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('multichoix.errors.load_failed', 'Erreur de chargement')}
        description={error.message}
        showIcon
      />
    );
  }

  const enemyTypes = data?.options || [];

  if (enemyTypes.length === 0) {
    return (
      <Empty
        description={t(
          'multichoix.steps.enemies.no_enemies',
          'Aucun ennemi disponible pour les filtres sélectionnés'
        )}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Mobile tree node renderer
  const renderMobileTreeNode = (node, enemyType, treeData, level = 0) => {
    const isExpanded = mobileExpandedNodes[`${enemyType}-${node.key}`];
    const hasChildren = node.children && node.children.length > 0;
    const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
    const isChecked = typeCheckedKeys.includes(node.key);

    return (
      <div key={node.key}>
        <div
          className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${
            isChecked ? 'bg-green-50' : ''
          }`}
          style={{ paddingLeft: `${12 + level * 16}px` }}
          onClick={() => {
            if (hasChildren) {
              setMobileExpandedNodes((prev) => ({
                ...prev,
                [`${enemyType}-${node.key}`]: !isExpanded,
              }));
            }
            // Toggle check for this node
            handleCheck(
              null,
              { node, checked: !isChecked },
              enemyType,
              treeData
            );
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown size={16} className="mr-2 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight size={16} className="mr-2 text-gray-500 flex-shrink-0" />
            )
          ) : (
            <div className="w-4 mr-2" />
          )}
          <div
            className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
              isChecked ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
            }`}
          >
            {isChecked && <Check size={14} className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs ${hasChildren ? 'font-medium text-[#207d54]' : 'text-gray-900'}`}>
              {node.title}
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) =>
              renderMobileTreeNode(child, enemyType, treeData, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // Mobile render
  if (isMobile) {
    return (
      <div className="space-y-3">
        {/* Language selector - compact mobile version */}
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-gray-600">
            {t('multichoix.steps.enemies.language', 'Langue')}:
          </span>
          <LanguageSelector
            value={language}
            options={languageOptions}
            onChange={(val) => {
              setLanguage(val);
              onForceRevalidation?.();
            }}
          />
        </div>

        {/* Enemy type cards */}
        {enemyTypes.map((enemyTypeData) => {
          const enemyType = enemyTypeData.enemy_type;
          const flatEnemies = enemyTypeData.children || [];

          let filteredEnemies = flatEnemies;
          if (enemyType === 'weeds' && weedCategoryFilters.length > 0) {
            filteredEnemies = filterByWeedCategory(flatEnemies, weedCategoryFilters);
          }

          let treeData;
          if (enemyType === 'disease') {
            if (diseaseListMode === 'grouping') {
              treeData = convertToTreeData(enemyTypeData.tree_by_grouping || [], enemyType);
            } else {
              treeData = convertToTreeData(enemyTypeData.tree_by_hierarchy || [], enemyType);
            }
          } else {
            treeData = convertToTreeData(filteredEnemies, enemyType);
          }

          const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
          const searchValue = searchValues[enemyType] || '';
          const filteredTreeData = filterTreeData(treeData, searchValue);

          return (
            <div key={enemyType} className="w-full">
              {/* Mobile trigger */}
              <div
                onClick={() => setMobileSheetOpen(enemyType)}
                className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900">
                    {enemyTypeLabels[enemyType] || enemyTypeData.label}
                  </div>
                  {typeCheckedKeys.length > 0 && (
                    <div className="text-xs text-[#207d54] mt-0.5">
                      {typeCheckedKeys.length} {t('multichoix.common.selected', 'sélectionné(s)')}
                    </div>
                  )}
                </div>
                <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
              </div>

              {/* Mobile sheet for this enemy type */}
              <MobileSheetBase
                isOpen={mobileSheetOpen === enemyType}
                onClose={() => setMobileSheetOpen(null)}
                title={enemyTypeLabels[enemyType] || enemyTypeData.label}
                showDone
                onDone={() => setMobileSheetOpen(null)}
              >
                {/* Search */}
                <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                      placeholder={t('multichoix.steps.enemies.search_placeholder', 'Rechercher...')}
                      value={searchValue}
                      onChange={(e) =>
                        setSearchValues((prev) => ({
                          ...prev,
                          [enemyType]: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Filters for diseases */}
                {enemyType === 'disease' && (
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`diseaseMode-${enemyType}`}
                          value="organism"
                          checked={diseaseListMode === 'organism'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#207d54]"
                        />
                        <span className="text-xs">
                          {t('multichoix.steps.enemies.list_by_hierarchy')}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`diseaseMode-${enemyType}`}
                          value="grouping"
                          checked={diseaseListMode === 'grouping'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#207d54]"
                        />
                        <span className="text-xs">
                          {t('multichoix.steps.enemies.list_by_grouping')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Weed category filters */}
                {enemyType === 'weeds' && weedGroups.length > 0 && (
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <div className="flex flex-wrap gap-2">
                      {weedGroups.map((group) => (
                        <label key={group.id} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={weedCategoryFilters.includes(group.id)}
                            onChange={(e) => handleWeedCategoryChange(group.id, e.target.checked)}
                            className="w-4 h-4 accent-[#207d54]"
                          />
                          <span className="text-xs">{group.label}</span>
                        </label>
                      ))}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={weedCategoryFilters.includes(WEED_NO_GROUP_VALUE)}
                          onChange={(e) => handleWeedCategoryChange(WEED_NO_GROUP_VALUE, e.target.checked)}
                          className="w-4 h-4 accent-[#207d54]"
                        />
                        <span className="text-xs">{t('multichoix.steps.enemies.weeds.other', 'Autres')}</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Select all */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <div
                    className="flex items-center min-h-[40px] cursor-pointer"
                    onClick={() => handleSelectAll(enemyType, treeData, !isAllSelectedForType(enemyType, treeData))}
                  >
                    <div
                      className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                        isAllSelectedForType(enemyType, treeData) ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                      }`}
                    >
                      {isAllSelectedForType(enemyType, treeData) && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-xs font-medium">
                      {t('multichoix.common.select_all', 'Sélectionner tout')}
                    </span>
                  </div>
                </div>

                {/* Tree options */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                  {filteredTreeData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                      <div className="text-xs">{t('multichoix.steps.enemies.no_results', 'Aucun ennemi trouvé')}</div>
                    </div>
                  ) : (
                    <div className="py-1">
                      {filteredTreeData.map((node) =>
                        renderMobileTreeNode(node, enemyType, treeData, 0)
                      )}
                    </div>
                  )}
                </div>

                {/* Selected tags at bottom */}
                {typeCheckedKeys.length > 0 && (
                  <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="text-xs text-gray-500 mb-2">
                      {t('multichoix.steps.enemies.selected_elements', 'Éléments sélectionnés:')}
                    </div>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {typeCheckedKeys.slice(0, 10).map((key) => {
                        const label = checkedLabels.find((l) => l.value === key);
                        const node = findNodeByKey(treeData, key);
                        const displayLabel = label?.label || (node ? getDisplayName(node.enemyData) : key);
                        return (
                          <Tag
                            key={key}
                            closable
                            onClose={(e) => {
                              e.preventDefault();
                              handleRemoveTag({ value: key });
                            }}
                            className="bg-[#5b8c00] border-[#5b8c00] text-white text-xs px-1.5 py-0.5"
                          >
                            {displayLabel}
                          </Tag>
                        );
                      })}
                      {typeCheckedKeys.length > 10 && (
                        <Tag className="bg-gray-200 text-gray-600 text-xs px-1.5 py-0.5">
                          +{typeCheckedKeys.length - 10}
                        </Tag>
                      )}
                    </div>
                  </div>
                )}
              </MobileSheetBase>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop render
  return (
    <div className="space-y-6">
      {enemyTypes.map((enemyTypeData) => {
        const enemyType = enemyTypeData.enemy_type;

        // Get flat enemy list from children
        const flatEnemies = enemyTypeData.children || [];

        // Apply weed category filter for weeds
        let filteredEnemies = flatEnemies;
        if (enemyType === 'weeds' && weedCategoryFilters.length > 0) {
          filteredEnemies = filterByWeedCategory(
            flatEnemies,
            weedCategoryFilters
          );
        }

        // Build tree based on enemy type and list mode
        let treeData;
        if (enemyType === 'disease') {
          // Use pre-built trees from API based on selected mode
          if (diseaseListMode === 'grouping') {
            treeData = convertToTreeData(enemyTypeData.tree_by_grouping || [], enemyType);
          } else {
            treeData = convertToTreeData(enemyTypeData.tree_by_hierarchy || [], enemyType);
          }
        } else {
          // For pests and weeds, use the default tree conversion
          treeData = convertToTreeData(filteredEnemies, enemyType);
        }
        const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
        const searchValue = searchValues[enemyType] || '';
        const filteredTreeData = filterTreeData(treeData, searchValue);
        // Build labels for selected items - use existing labels if they have enemyType, otherwise build from tree
        const typeSelectedLabels = typeCheckedKeys.map((id) => {
          const existingLabel = checkedLabels.find((l) => l.value === id);
          if (existingLabel && existingLabel.enemyType === enemyType) {
            return existingLabel;
          }
          // Cold start fallback: build label from tree data
          const node = findNodeByKey(treeData, id);
          return {
            value: id,
            label: node ? getDisplayName(node.enemyData) : id,
            enemyType,
          };
        });
        const isAllSelected = isAllSelectedForType(enemyType, treeData);

        return (
          <div key={enemyType} className="rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-800">
                    {enemyTypeLabels[enemyType] || enemyTypeData.label}
                  </span>
                  <Tooltip
                    title={t(
                      'multichoix.steps.enemies.tooltip',
                      'Sélectionnez les ennemis à cibler'
                    )}
                  >
                    <InfoCircleOutlined className="text-gray-500" />
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Content - 50/50 layout */}
            <div className="flex flex-col lg:flex-row">
              {/* Left side - Tree (50%) */}
              <div className="flex-1 p-4 green-checkbox">
                {/* Label and Language row */}
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-600">
                    {t(
                      'multichoix.steps.enemies.search_label',
                      "Recherche par nom d'ennemies"
                    )}
                  </label>
                  <LanguageSelector
                    value={language}
                    options={languageOptions}
                    onChange={(val) => {
                      setLanguage(val);
                      onForceRevalidation?.();
                    }}
                  />
                </div>
                {/* Search input - full width */}
                <div className="mb-3">
                  <TreeSearchInput
                    value={searchValue}
                    onChange={(val) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [enemyType]: val,
                      }))
                    }
                    placeholder={t(
                      'multichoix.steps.enemies.search_placeholder',
                      'Rechercher...'
                    )}
                    width="100%"
                  />
                </div>

                {/* List mode toggle - always visible for diseases */}
                {enemyType === 'disease' && (
                  <div className="mb-3 flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="diseaseListMode"
                        value="organism"
                        checked={diseaseListMode === 'organism'}
                        onChange={(e) => setDiseaseListMode(e.target.value)}
                        className="w-4 h-4 accent-[#5b8c00]"
                      />
                      <span className="text-sm">
                        {t('multichoix.steps.enemies.list_by_hierarchy')}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="diseaseListMode"
                        value="grouping"
                        checked={diseaseListMode === 'grouping'}
                        onChange={(e) => setDiseaseListMode(e.target.value)}
                        className="w-4 h-4 accent-[#5b8c00]"
                      />
                      <span className="text-sm">
                        {t('multichoix.steps.enemies.list_by_grouping')}
                      </span>
                    </label>
                  </div>
                )}

                {/* Weed category filters - only for weeds */}
                {enemyType === 'weeds' && weedGroups.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-3">
                    {/* Dynamic checkboxes from weed groups API */}
                    {weedGroups.map((group) => (
                      <Checkbox
                        key={group.id}
                        checked={weedCategoryFilters.includes(group.id)}
                        onChange={(e) =>
                          handleWeedCategoryChange(group.id, e.target.checked)
                        }
                      >
                        {group.label}
                      </Checkbox>
                    ))}
                    {/* "Autres" checkbox for weeds without a group */}
                    <Checkbox
                      key={WEED_NO_GROUP_VALUE}
                      checked={weedCategoryFilters.includes(
                        WEED_NO_GROUP_VALUE
                      )}
                      onChange={(e) =>
                        handleWeedCategoryChange(
                          WEED_NO_GROUP_VALUE,
                          e.target.checked
                        )
                      }
                    >
                      {t('multichoix.steps.enemies.weeds.other', 'Autres')}
                    </Checkbox>
                  </div>
                )}

                {/* Select all checkbox */}
                <div className="mb-3 pb-2 border-b border-gray-100">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) =>
                      handleSelectAll(enemyType, treeData, e.target.checked)
                    }
                  >
                    {t('multichoix.common.select_all', 'Sélectionner tout')}
                  </Checkbox>
                </div>

                {/* Tree */}
                <div className="max-h-[350px] overflow-y-auto">
                  {filteredTreeData.length > 0 ? (
                    <Tree
                      checkable
                      checkStrictly
                      checkedKeys={{
                        checked: typeCheckedKeys,
                        halfChecked: [],
                      }}
                      onCheck={(keys, info) =>
                        handleCheck(keys, info, enemyType, treeData)
                      }
                      expandedKeys={expandedKeys[enemyType] || []}
                      onExpand={(keys) => handleExpand(keys, enemyType)}
                      treeData={filteredTreeData}
                      selectable={false}
                      blockNode
                      className="enemy-tree"
                    />
                  ) : (
                    <Empty
                      description={t(
                        'multichoix.steps.enemies.no_results',
                        'Aucun ennemi trouvé'
                      )}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </div>
              </div>

              {/* Right side - Selected elements (50%) */}
              <div className="flex-1 p-4">
                <fieldset className="border border-[#c4c49a] rounded min-h-[200px]">
                  <legend className="ml-2 px-2 text-sm text-gray-700 font-medium">
                    {t(
                      'multichoix.steps.enemies.selected_elements',
                      'Éléments sélectionnés:'
                    )}
                  </legend>
                  <div className="p-3">
                    {typeSelectedLabels.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {typeSelectedLabels.map((item) => (
                          <Tag
                            key={item.value}
                            closable
                            onClose={() => handleRemoveTag(item)}
                            className="bg-[#5b8c00] border-[#5b8c00] text-white px-2 py-0.5 text-sm"
                          >
                            {item.label}
                          </Tag>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">
                        {t(
                          'multichoix.steps.enemies.no_selection',
                          'Aucun élément sélectionné'
                        )}
                      </p>
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EnemiesStepContent;
